<template>
  <StandardCardPageContainer>
    <template #title>Users</template>
    <LoadingCircle v-if="isLoading" />
    <div v-else>
      <v-row>
        <v-col>
          <o-table :data="users">
            <o-table-column v-for="column in columns" v-bind="column">
              <template #default="props">
                <v-row no-gutters class="align-center">
                  <AvatarContainer
                    v-if="column.field === 'name'"
                    class="mr-2"
                    :name="props.row[column.field]"
                    size="small"
                  />
                  <PPopUpMenu
                    v-if="column.field === 'menuIcon' && users.length > 1"
                    :menu-items="menuItems"
                    :menu-icon="'icon-horizontal-more-outline'"
                    @menu-item-clicked="removeUser(props.row)"
                  ></PPopUpMenu>
                  {{ props.row[column.field] }}
                </v-row>
              </template>
            </o-table-column>
          </o-table>
        </v-col>
      </v-row>

      <div class="text-right mt-5">
        <PPrimaryButton
          appkit-icon="icon-plus-fill"
          @click="isDialogOpen = true"
          >Add user</PPrimaryButton
        >
      </div>

      <PModalBox
        header="Invite user"
        :open-dialog="isDialogOpen"
        @close-dialog="cancelInvite"
      >
        <template #content>
          <v-row>
            <v-col cols="6">
              <PInputField
                label="Name"
                :value="state.newUser.name"
                @update-event="(val) => (state.newUser.name = val)"
              />
            </v-col>
            <v-col cols="6">
              <PInputField
                label="Email"
                :value="state.newUser.email"
                :error="v.newUser.email.$error"
                :error-messages="v.newUser.$errors[0]?.$message.toString()"
                @update-event="(val) => (state.newUser.email = val)"
              />
            </v-col>
          </v-row>
        </template>

        <template #leftbutton>
          <PSecondaryButton class="mr-2" @click="cancelInvite"
            >Cancel</PSecondaryButton
          >
        </template>

        <template #rightbutton>
          <PPrimaryButton @click="addUser">Add</PPrimaryButton>
        </template>
      </PModalBox>
    </div>
  </StandardCardPageContainer>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import {
  addUserToTrustGroup,
  getTrustGroupUsers,
} from "@/services/trustGroupService";
import { TrustGroupUserDto } from "@/models/dto/trustGroupUserDto";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import PInputField from "@/components/custom/PInputField.vue";
import AvatarContainer from "@/components/custom/AvatarContainer.vue";
import PSecondaryButton from "@/components/custom/buttons/PSecondaryButton.vue";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import { useSnackBarStore } from "@/stores/snackBarStore";
import useVuelidate from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";
import PPopUpMenu from "@/components/custom/PPopUpMenu.vue";
import { RouterPath } from "@/models/enum/routerPath";
import { useTrustGroupStore } from "@/stores/trustGroup";
import PModalBox from "@/components/custom/PModalBox.vue";

const snackbarStore = useSnackBarStore();
const trustGroupStore = useTrustGroupStore();

const isLoading = ref(false);
const isDialogOpen = ref(false);
const newUser = ref(new TrustGroupUserDto());
const users = ref(new Array<TrustGroupUserDto>());

const menuItems = ref([
  {
    id: "Remove",
    title: "Remove user",
    icon: "icon-delete-outline",
    path: RouterPath.Team,
    visible: true,
  },
]);

const columns = ref([
  {
    field: "name",
    label: "Name",
    sortable: true,
  },
  {
    field: "email",
    label: "Email",
    sortable: true,
  },
  {
    field: "menuIcon",
  },
]);

const state = ref({ newUser });
const rules = {
  newUser: {
    email: { required, email },
  },
};

const v = useVuelidate(rules, state);

onBeforeMount(async () => await loadView());

async function addUser(): Promise<void> {
  const validForm = await v.value.$validate();
  if (!validForm) {
    return;
  }

  try {
    await addUserToTrustGroup(state.value.newUser);
    snackbarStore.showSuccess("User added");
  } catch {
    snackbarStore.showError("Transaction failed");
  } finally {
    isDialogOpen.value = false;
    newUser.value = new TrustGroupUserDto();
    state.value.newUser = newUser.value;
    v.value.$reset();
    await loadView();
  }
}

async function loadView(): Promise<void> {
  isLoading.value = true;
  users.value = await getTrustGroupUsers();
  isLoading.value = false;
}

function cancelInvite(): void {
  isDialogOpen.value = false;
  newUser.value = new TrustGroupUserDto();
  state.value.newUser = newUser.value;
  v.value.$reset();
}

async function removeUser(user: TrustGroupUserDto): Promise<void> {
  try {
    await trustGroupStore.removeUserFromTrustGroup(user);
    await loadView();
  } catch (error: any) {
    snackbarStore.showError(error.message);
  }
}
</script>

<style scoped lang="scss">
.table--border > th {
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
